import { create } from "zustand";

/**
 * Single, minimal Zustand store for the best-of-5 reaction test (hard rule: one
 * store per app). State machine:
 *
 *   idle → waiting (red, random delay) → ready (green) → [tap]
 *     ready  + tap  → record ms; round 5 ⇒ done, else next round → waiting
 *     waiting + tap → tooSoon (early tap; round NOT counted, retry)
 *
 * Timing uses performance.now() (monotonic). The random-delay timer lives in the
 * component, which calls flip() when it fires — the store stays pure/testable.
 */

export const ROUNDS = 5;
export const MIN_DELAY_MS = 1500;
export const MAX_DELAY_MS = 4500;

export type Phase = "idle" | "waiting" | "ready" | "tooSoon" | "done";

interface ReflexState {
  phase: Phase;
  /** Number of counted rounds completed (0..ROUNDS). */
  completed: number;
  /** ms for each counted round, in order. length === completed. */
  rounds: number[];
  /** performance.now() captured at the green flip; null outside a ready window. */
  greenAt: number | null;
  /** ms of the most recent counted tap (for per-round feedback). */
  lastMs: number | null;
  /** Lowest ms across counted rounds = the brag number. null until first round. */
  bestMs: number | null;

  /** Begin a run (or the next round): idle/feedback → waiting. */
  arm: () => void;
  /** Random-delay timer fired: waiting → ready, stamp greenAt. */
  flip: (at: number) => void;
  /** Player tapped at time `at` (performance.now()). */
  tap: (at: number) => void;
  /** Retry the current (uncounted) round after an early tap. */
  retry: () => void;
  /** Full reset back to idle for a fresh run. */
  reset: () => void;

  /** All-time best (ms) before this run, from localStorage. null if first ever. */
  prevBest: number | null;
  /** True once a finished run beats prevBest — drives the "NEW BEST" celebration. */
  isNewBest: boolean;
}

const PB_KEY = "kp:reflex:best";

function loadPrevBest(): number | null {
  try {
    const v = localStorage.getItem(PB_KEY);
    return v === null ? null : Number.parseInt(v, 10);
  } catch {
    return null;
  }
}

function savePrevBest(ms: number): void {
  try {
    localStorage.setItem(PB_KEY, String(ms));
  } catch {
    /* storage blocked — fine */
  }
}

const initial = {
  phase: "idle" as Phase,
  completed: 0,
  rounds: [] as number[],
  greenAt: null as number | null,
  lastMs: null as number | null,
  bestMs: null as number | null,
  isNewBest: false,
};

export const useReflexStore = create<ReflexState>((set, get) => ({
  ...initial,
  prevBest: loadPrevBest(),

  arm: () => set({ phase: "waiting", greenAt: null }),

  flip: (at) => set({ phase: "ready", greenAt: at }),

  tap: (at) => {
    const { phase, greenAt, rounds, bestMs, prevBest } = get();
    if (phase === "waiting") {
      // Tapped before green — does not count.
      set({ phase: "tooSoon", greenAt: null });
      return;
    }
    if (phase !== "ready" || greenAt === null) return; // ignore stray taps
    const ms = Math.round(at - greenAt);
    const nextRounds = [...rounds, ms];
    const nextBest = bestMs === null ? ms : Math.min(bestMs, ms);
    const done = nextRounds.length >= ROUNDS;

    if (!done) {
      set({
        phase: "waiting",
        completed: nextRounds.length,
        rounds: nextRounds,
        greenAt: null,
        lastMs: ms,
        bestMs: nextBest,
      });
      return;
    }

    // Run finished — did this beat the all-time best? (lower is better)
    const beat = prevBest === null || nextBest < prevBest;
    if (beat) savePrevBest(nextBest);
    set({
      phase: "done",
      completed: nextRounds.length,
      rounds: nextRounds,
      greenAt: null,
      lastMs: ms,
      bestMs: nextBest,
      isNewBest: beat,
    });
  },

  retry: () => set({ phase: "waiting", greenAt: null }),

  // Reload prevBest (it may have just advanced) and clear the celebration flag.
  reset: () => set({ ...initial, prevBest: loadPrevBest() }),
}));

/** Random delay for the next waiting→ready flip. */
export function randomDelayMs(): number {
  // No crypto needed — Math.random is fine for game jitter (not the score itself).
  return MIN_DELAY_MS + Math.floor(Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS));
}
